/**
 * Página /resources (RECURSOS, estilo Tiflux): busca por nome/serial/IMEI,
 * filtros por cliente e tipo, chips Ativos|Inativos|Todos, tabela compacta com
 * ações no hover, dialog de detalhe, drawer '+ Recurso' e gear 'Tipos de
 * recurso'. Paginação Anterior/Próxima via X-Total-Items (limit 50).
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTenantProfile } from '@/shared/tenant-profile';
import { Pencil, Plus, Search, Settings, Trash2 } from 'lucide-react';
import type { Equipment } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { ClientFilter } from '@/features/billing/client-filter';
import type { ClientRef } from '@/features/tickets/ClientPicker';
import { useDebouncedValue } from '@/features/tickets/use-debounced-value';
import {
  RESOURCES_PAGE_SIZE,
  equipmentListOptions,
  equipmentTypesOptions,
  errorMessage,
} from './resources-queries';
import { useDeleteEquipment } from './resources-mutations';
import { EquipmentStatusBadge, EquipmentWarranty } from './resources-format';
import { ResourceDrawer } from './resource-drawer';
import { ResourceTypesDialog } from './resource-types-dialog';
import { ResourceDetailDialog } from './resource-detail-dialog';

type ActiveFilter = 'active' | 'inactive' | 'all';

const ACTIVE_FILTERS: { value: ActiveFilter; label: string }[] = [
  { value: 'active', label: 'Ativos' },
  { value: 'inactive', label: 'Inativos' },
  { value: 'all', label: 'Todos' },
];

type DrawerState = { mode: 'new' } | { mode: 'edit'; equipment: Equipment };

function deleteErrorMessage(err: unknown): string {
  if (err !== null && typeof err === 'object' && 'error' in err) {
    const code = (err as { error: unknown }).error;
    if (code === 'equipment_in_use') return 'Há tickets vinculados a este recurso';
  }
  return errorMessage(err);
}

export function ResourcesPage() {
  const [term, setTerm] = useState('');
  const search = useDebouncedValue(term, 300);
  const [client, setClient] = useState<ClientRef | null>(null);
  const [typeId, setTypeId] = useState('');
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('active');
  const [page, setPage] = useState(1);

  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [typesOpen, setTypesOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [toDelete, setToDelete] = useState<Equipment | null>(null);
  // spec 032: o veículo do SGR abre a página dele (com os tickets); o resto, o detalhe
  const navigate = useNavigate();
  const terms = useTenantProfile().terms;
  const contactLabel = terms.contact.charAt(0).toUpperCase() + terms.contact.slice(1);
  const openRow = (r: Equipment) => {
    if (r.sgrId) void navigate({ to: '/resources/$resourceId', params: { resourceId: String(r.id) } });
    else setDetailId(r.id);
  };

  // Qualquer mudança de filtro volta para a primeira página.
  useEffect(() => {
    setPage(1);
  }, [search, client, typeId, activeFilter]);

  const typesQ = useQuery(equipmentTypesOptions());
  const listQ = useQuery(
    equipmentListOptions({
      search: search.trim() || undefined,
      client_ids: client ? [client.id] : undefined,
      type_ids: typeId !== '' ? [Number(typeId)] : undefined,
      active: activeFilter === 'all' ? undefined : activeFilter === 'active',
      limit: RESOURCES_PAGE_SIZE,
      offset: (page - 1) * RESOURCES_PAGE_SIZE,
    })
  );
  // a coluna do associado só quando a página tem algum veículo do SGR
  const showContact = (listQ.data?.items ?? []).some((r) => r.endCustomer);
  const del = useDeleteEquipment();

  const total = listQ.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / RESOURCES_PAGE_SIZE));

  const closeDelete = () => {
    setToDelete(null);
    del.reset();
  };

  return (
    <div className="overflow-hidden rounded-lg border bg-card shadow-xs">
      {/* Cabeçalho: título + contagem + gear tipos + CTA */}
      <div className="flex flex-wrap items-center gap-3 p-4 pb-3">
        <h1 className="text-xl leading-7 font-semibold">Recursos</h1>
        {listQ.data ? (
          <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground tabular-nums">
            {total}
          </span>
        ) : null}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setTypesOpen(true)}>
            <Settings />
            Tipos de recurso
          </Button>
          <Button size="sm" onClick={() => setDrawer({ mode: 'new' })}>
            <Plus />
            Recurso
          </Button>
        </div>
      </div>

      {/* Toolbar: busca + cliente + tipo + chips */}
      <div className="flex flex-wrap items-center gap-2 px-4 pb-3">
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Buscar por nome, serial ou IMEI"
            aria-label="Buscar recursos"
            className="h-8 w-64 pl-8"
          />
        </div>
        <ClientFilter selected={client} onSelect={setClient} />
        <Select
          value={typeId}
          onChange={(e) => setTypeId(e.target.value)}
          aria-label="Filtrar por tipo"
          className="h-8 w-44"
        >
          <option value="">Todos os tipos</option>
          {(typesQ.data ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </Select>
        <div className="ml-auto flex items-center gap-2">
          {ACTIVE_FILTERS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              aria-pressed={activeFilter === value}
              onClick={() => setActiveFilter(value)}
              className={cn(
                'inline-flex h-8 items-center rounded-md border px-3 text-[13px] font-medium transition-colors',
                activeFilter === value
                  ? 'border-transparent bg-foreground text-background'
                  : 'border-border bg-card text-foreground hover:bg-muted/60'
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Tabela */}
      {listQ.isPending ? (
        <div aria-busy className="space-y-3 border-t border-divider p-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton className="h-4 w-1/5" />
              <Skeleton className="h-4 w-1/6" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-5 w-16 rounded-sm" />
            </div>
          ))}
        </div>
      ) : listQ.isError ? (
        <div className="border-t border-divider">
          <EmptyState message={`Erro ao carregar recursos: ${errorMessage(listQ.error)}`} />
        </div>
      ) : listQ.data.items.length === 0 ? (
        <div className="border-t border-divider">
          <EmptyState
            message={
              search.trim() || client || typeId !== ''
                ? 'Nenhum recurso encontrado para os filtros'
                : 'Nenhum recurso cadastrado'
            }
          />
        </div>
      ) : (
        <div className="overflow-x-auto border-t border-divider">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted text-left text-[13px]">
                <th className="h-9 px-4 font-semibold">Nome</th>
                <th className="h-9 px-4 font-semibold">Cliente</th>
                {showContact ? <th className="h-9 px-4 font-semibold">{contactLabel}</th> : null}
                <th className="h-9 px-4 font-semibold">Tipo</th>
                <th className="h-9 px-4 font-semibold">Grupo</th>
                <th className="h-9 px-4 font-semibold">Serial</th>
                <th className="h-9 px-4 font-semibold">IMEI</th>
                <th className="h-9 px-4 font-semibold">Garantia</th>
                <th className="h-9 px-4 font-semibold">Status</th>
                <th className="h-9 px-4 text-right font-semibold">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {listQ.data.items.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => openRow(r)}
                  tabIndex={0}
                  role="link"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') openRow(r);
                  }}
                  className="group h-10 cursor-pointer border-b border-divider transition-colors last:border-0 hover:bg-accent/60 focus-visible:bg-accent/60 focus-visible:outline-none"
                >
                  <td className="max-w-56 truncate px-4 font-medium" title={r.name}>
                    {r.name}
                  </td>
                  <td className="max-w-44 truncate px-4" title={r.clientName ?? undefined}>
                    {r.clientName ?? '—'}
                  </td>
                  {showContact ? (
                    <td className="max-w-44 truncate px-4" title={r.endCustomer?.name}>
                      {r.endCustomer?.name ?? '—'}
                    </td>
                  ) : null}
                  <td className="px-4 whitespace-nowrap text-muted-foreground">
                    {r.equipmentTypeName}
                  </td>
                  <td className="max-w-36 truncate px-4 text-muted-foreground">
                    {r.equipmentGroupName ?? '—'}
                  </td>
                  <td className="px-4 whitespace-nowrap text-muted-foreground tabular-nums">
                    {r.serialNumber ?? '—'}
                  </td>
                  <td className="px-4 whitespace-nowrap text-muted-foreground tabular-nums">
                    {r.imei ?? '—'}
                  </td>
                  <td className="px-4 whitespace-nowrap">
                    <EquipmentWarranty equipment={r} />
                  </td>
                  <td className="px-4 whitespace-nowrap">
                    <EquipmentStatusBadge active={r.active} />
                  </td>
                  <td className="px-4">
                    <div className="flex justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        title="Editar recurso"
                        aria-label={`Editar recurso ${r.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDrawer({ mode: 'edit', equipment: r });
                        }}
                      >
                        <Pencil />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        title="Excluir recurso"
                        aria-label={`Excluir recurso ${r.name}`}
                        className="text-destructive hover:bg-destructive/5 hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          setToDelete(r);
                        }}
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Rodapé: contagem + paginação Anterior/Próxima */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-divider px-4 py-3">
        <span className="text-[13px] text-muted-foreground">
          {total} recurso{total === 1 ? '' : 's'} — página {page} de {pageCount}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Anterior
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= pageCount}
            onClick={() => setPage((p) => p + 1)}
          >
            Próxima
          </Button>
        </div>
      </div>

      {drawer ? (
        <ResourceDrawer
          initial={drawer.mode === 'edit' ? drawer.equipment : undefined}
          onClose={() => setDrawer(null)}
        />
      ) : null}
      {typesOpen ? <ResourceTypesDialog onClose={() => setTypesOpen(false)} /> : null}
      {detailId !== null ? (
        <ResourceDetailDialog
          equipmentId={detailId}
          onEdit={(eq) => {
            setDetailId(null);
            setDrawer({ mode: 'edit', equipment: eq });
          }}
          onClose={() => setDetailId(null)}
        />
      ) : null}

      {toDelete ? (
        <Dialog open onClose={closeDelete} title="Excluir recurso">
          <p className="text-sm text-muted-foreground">
            Excluir o recurso{' '}
            <span className="font-medium text-foreground">{toDelete.name}</span> de{' '}
            {toDelete.clientName}? Esta ação não pode ser desfeita.
          </p>
          {del.error ? (
            <p className="mt-2 text-sm text-destructive">{deleteErrorMessage(del.error)}</p>
          ) : null}
          <div className="mt-4 flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={closeDelete}>
              Voltar
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={del.isPending}
              onClick={() => del.mutate(toDelete.id, { onSuccess: closeDelete })}
            >
              {del.isPending ? 'Excluindo…' : 'Excluir recurso'}
            </Button>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
